import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Select } from 'antd';
import { useAuth } from '../../context/AuthContext';
import { addProjectToWorkspace, listMyProjects } from '../../services/WorkspacesService';

/**
 * "Add project" on a workspace's page: one of the person's own projects not in this workspace (one in another stays
 * there too: a project may be in several). Only their own: a project is added by its owner.
 */
export default function AddProjectToWorkspace({ workspace, onAdded }) {
  const { user } = useAuth();
  const [projects, setProjects] = useState(null);
  const [choice, setChoice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user?.sub) { return undefined; }
    let alive = true;
    listMyProjects(user.sub)
      .then((list) => { if (alive) { setProjects(list); } })
      .catch((failed) => { if (alive) { setError(failed.message); setProjects([]); } });
    return () => { alive = false; };
  }, [user?.sub, workspace.projects.length]);

  const inHere = new Set(workspace.projects.map((project) => project.projectId));
  const options = (projects || []).filter((project) => !inHere.has(project.projectId)).map((project) => {
    const others = project.workspaces || (project.workspace ? [project.workspace] : []);
    return { value: project.projectId, label: others.length ? `${project.name} (also in ${others.map((ws) => ws.name).join(', ')})` : project.name };
  });

  const add = async() => {
    setSaving(true);
    setError(null);
    try {
      await addProjectToWorkspace(workspace.id, choice);
      setChoice(null);
      onAdded();
    } catch (failed) {
      setError(failed.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="workspace-add">
      <Select
        aria-label="Project to add"
        className="workspace-add-select"
        loading={projects === null}
        notFoundContent="No project of yours to add"
        onChange={setChoice}
        options={options}
        placeholder="Add one of your projects…"
        showSearch
        optionFilterProp="label"
        value={choice}
      />
      <Button disabled={!choice} loading={saving} onClick={add} type="primary">Add project</Button>
      {error && <p className="sbx-action-error" role="alert">{error}</p>}
    </div>
  );
}

AddProjectToWorkspace.propTypes = {
  workspace: PropTypes.shape({ id: PropTypes.string.isRequired, projects: PropTypes.array.isRequired }).isRequired,
  onAdded: PropTypes.func.isRequired,
};
